import React, { useEffect, useState } from 'react';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { Pencil, Trash2, X } from 'lucide-react';

const empty = { name: '', contact_email: '', status: 'active', license_seats: 1 };

const AdminCompanies = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await adminAPI.listCompanies();
      setCompanies(data.companies);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(empty); setModalOpen(true); };
  const openEdit = (c) => {
    setEditingId(c.id);
    setForm({ name: c.name, contact_email: c.contact_email, status: c.status, license_seats: c.license_seats });
    setModalOpen(true);
  };
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, license_seats: Number(form.license_seats) };
      if (editingId) {
        await adminAPI.updateCompany(editingId, payload);
        toast.success('Company updated');
      } else {
        await adminAPI.createCompany(payload);
        toast.success('Company added');
      }
      setModalOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Remove this company?')) return;
    try {
      await adminAPI.deleteCompany(id);
      toast.success('Company removed');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  if (loading)
    return <div className="p-8 text-sm" style={{ color: 'var(--c-ink-2)' }}>Loading companies…</div>;

  return (
    <div className="p-8" data-testid="admin-companies">
      <div className="page-header -mx-8 -mt-8 mb-8 px-8 py-6">
        <div>
          <p className="eyebrow">Portal administration</p>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif', color: 'var(--c-ink)' }}>
            Companies
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--c-ink-2)' }}>
            Onboard, edit, or suspend the firms using the platform.
          </p>
        </div>
      </div>

      <div className="bento-card p-0 overflow-hidden">
        {companies.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm" style={{ color: 'var(--c-ink-3)' }}>
            No companies onboarded yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="companies-table">
            <thead>
              <tr className="data-table-header">
                <th className="text-left px-6 py-3 font-semibold">Company</th>
                <th className="text-left px-6 py-3 font-semibold">Contact</th>
                <th className="text-left px-6 py-3 font-semibold">Status</th>
                <th className="text-right px-6 py-3 font-semibold">Seats</th>
                <th className="text-right px-6 py-3 font-semibold">Joined</th>
                <th className="text-right px-6 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} style={{ borderTop: '1px solid var(--c-line)' }}>
                  <td className="px-6 py-3" style={{ color: 'var(--c-ink)', fontWeight: 500 }}>{c.name}</td>
                  <td className="px-6 py-3" style={{ color: 'var(--c-ink-2)' }}>{c.contact_email}</td>
                  <td className="px-6 py-3">
                    <span className={`badge ${c.status === 'active' ? 'badge-success' : 'badge-error'}`}>{c.status}</span>
                  </td>
                  <td className="px-6 py-3 text-right metric">{c.license_seats}</td>
                  <td className="px-6 py-3 text-right text-xs" style={{ color: 'var(--c-ink-3)' }}>
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-right">
                    <button className="p-1.5 rounded hover:bg-[var(--c-bone)] mr-1" title="Edit" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                      <Pencil className="w-4 h-4" style={{ color: 'var(--c-ink-2)' }} strokeWidth={1.75} />
                    </button>
                    <button className="p-1.5 rounded hover:bg-[#FCEFEA]" title="Delete" onClick={() => remove(c.id)} data-testid={`delete-company-${c.id}`}>
                      <Trash2 className="w-4 h-4" style={{ color: 'var(--c-terracotta)' }} strokeWidth={1.75} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: 'rgba(17, 17, 17, 0.4)', backdropFilter: 'blur(2px)' }}
          onClick={() => setModalOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white border w-full max-w-md"
            style={{ borderColor: 'var(--c-line)', borderRadius: 6, boxShadow: '0 24px 60px -24px rgba(0,0,0,0.25)' }}
            data-testid="company-modal"
          >
            <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--c-line)' }}>
              <p className="eyebrow">{editingId ? 'Edit company' : 'New company'}</p>
              <button onClick={() => setModalOpen(false)} className="p-1 hover:bg-[var(--c-bone)] rounded">
                <X className="w-4 h-4" style={{ color: 'var(--c-ink-2)' }} />
              </button>
            </div>
            <form onSubmit={submit} className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--c-ink-2)' }}>
                  Company name
                </label>
                <input required type="text" className="form-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name-input" placeholder="Acme Architects" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--c-ink-2)' }}>
                  Contact email
                </label>
                <input required type="email" className="form-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="company-email-input" placeholder="contact@company.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--c-ink-2)' }}>Status</label>
                  <select className="form-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="company-status-input">
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'var(--c-ink-2)' }}>Seats</label>
                  <input required type="number" min="0" className="form-input" value={form.license_seats} onChange={(e) => setForm({ ...form, license_seats: e.target.value })} data-testid="company-seats-input" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)} data-testid="cancel-company-btn">Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="save-company-btn">
                  {saving ? 'Saving…' : editingId ? 'Save changes' : 'Add company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompanies;
